import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ontologyApi } from '@/lib/api';
import { queryKeys } from '../client';
import type { Ontology } from '@shared/types/ontology';

/**
 * Hook to fetch all ontologies
 */
export function useOntologies() {
  return useQuery({
    queryKey: queryKeys.ontologies,
    queryFn: () => ontologyApi.getOntologies(),
  });
}

/**
 * Hook to fetch a single ontology
 */
export function useOntology(id: string) {
  return useQuery({
    queryKey: queryKeys.ontology(id),
    queryFn: () => ontologyApi.getOntology(id),
    enabled: !!id,
  });
}

/**
 * Hook to create an ontology
 */
export function useCreateOntology() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      metadata?: Record<string, any>;
    }) => ontologyApi.createOntology(data),
    onSuccess: () => {
      // Invalidate ontologies list
      queryClient.invalidateQueries({ queryKey: queryKeys.ontologies });
    },
  });
}

/**
 * Hook to update an ontology
 */
export function useUpdateOntology(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name?: string;
      description?: string;
      metadata?: Record<string, any>;
    }) => ontologyApi.updateOntology(id, data),
    onSuccess: (updatedOntology) => {
      // Update cache
      queryClient.setQueryData<Ontology>(
        queryKeys.ontology(id),
        updatedOntology
      );
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: queryKeys.ontologies });
    },
  });
}

/**
 * Hook to delete an ontology
 */
export function useDeleteOntology() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ontologyApi.deleteOntology(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.ontology(id) });
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: queryKeys.ontologies });
    },
  });
}

// Made with Bob